import { useEffect, useState, useCallback, useRef } from 'react';
import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness'; 
import '@aws-amplify/ui-react/styles.css';
import './Liveness.css';

function Liveness() {
  const [sessionId, setSessionId] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [config, setConfig] = useState(null);
  const [region] = useState(process.env.REACT_APP_AWS_REGION);
  const [error, setError] = useState(null);
  const [start, setStart] = useState(false);
  const parentOriginRef = useRef(null);

  // Send message to parent
  const sendMessageToParent = useCallback((type, payload) => {
    const targetOrigin = parentOriginRef.current;
    if (!targetOrigin) {
      console.warn('Parent origin not set, cannot send message');
      return;
    }
    
    if (window.parent) {
      window.parent.postMessage({ type, payload }, targetOrigin);
    }
  }, []);

  // Handle messages from parent
  const handleMessageEvent = useCallback((event) => {
    console.log("new var REACT_APP_ALLOWED_ORIGIN_URLS: ",process.env.REACT_APP_ALLOWED_ORIGIN_URLS)
    const allowedOrigins = process.env.REACT_APP_ALLOWED_ORIGINS 
      ? process.env.REACT_APP_ALLOWED_ORIGINS.split(',')
      : console.error('REACT_APP_ALLOWED_ORIGINS is not defined');
    
    if (!allowedOrigins.includes(event.origin)) {
      console.warn('Message from unauthorized origin:', event.origin);
      return;
    }

    let { type, payload } = event.data;

    switch (type) {
      case 'SET_ORIGIN':
        parentOriginRef.current = payload.message || payload.origin || event.origin;
        sendMessageToParent('ORIGIN_SET', { 
          success: true,
          origin: parentOriginRef.current 
        });
        break;

      case 'SET_CREDENTIALS':
        try {
          if (!payload.sessionId || !payload.credentials) {
            throw new Error('Missing sessionId or credentials');
          }

          const normalizedCredentials = {
            accessKeyId: payload.credentials.accessKeyId || payload.credentials.AccessKeyId,
            secretAccessKey: payload.credentials.secretAccessKey || payload.credentials.SecretAccessKey,
            sessionToken: payload.credentials.sessionToken || payload.credentials.SessionToken,
            expiration: new Date(payload.credentials.expiration) || new Date(payload.credentials.Expiration)
          };

          const livenessConfig = {
            credentialProvider: async () => normalizedCredentials
          };

          setSessionId(payload.sessionId);
          setCredentials(normalizedCredentials);
          setConfig(livenessConfig);
          setError(null);
          sendMessageToParent('CREDENTIALS_SET', { 
            success: true,
            sessionId: payload.sessionId 
          });
          setStart(true)
        } catch (err) {
          console.error('Error setting credentials:', err);
          setError(err.message);
          sendMessageToParent('CREDENTIALS_SET', { 
            success: false,
            error: err.message 
          });
        }
        break;

      case 'START_LIVENESS':
        if (!sessionId || !credentials) {
          sendMessageToParent('LIVENESS_ERROR', {
            error: 'Credentials not set. Please call SET_CREDENTIALS first.'
          });
        }
        break;

      case 'RESET':
        setSessionId(null);
        setCredentials(null);
        setConfig(null);
        setError(null);
        sendMessageToParent('RESET_COMPLETE', { success: true });
        break;

      default:
        // console.log('Unknown message type:', type);
    }
  }, [sendMessageToParent]); // Removed sessionId and credentials from dependencies

  // Discover the parent's origin before SET_ORIGIN arrives. Query param works on
  // iOS/Android/file:// (parent controls the iframe src); ancestorOrigins is
  // Chromium-only; referrer is unreliable in WKWebView/file:// and used last.
  const detectParentOrigin = useCallback((allowedOrigins) => {
    try {
      const paramOrigin = new URLSearchParams(window.location.search).get('parentOrigin');
      if (paramOrigin && allowedOrigins.includes(paramOrigin)) {
        return paramOrigin;
      }
    } catch (err) {
      console.warn('Could not read parentOrigin query param:', err);
    }

    const ancestorOrigin = window.location.ancestorOrigins && window.location.ancestorOrigins[0];
    if (ancestorOrigin && allowedOrigins.includes(ancestorOrigin)) {
      return ancestorOrigin;
    }

    if (document.referrer) {
      try {
        const referrerOrigin = new URL(document.referrer).origin;
        if (allowedOrigins.includes(referrerOrigin)) {
          return referrerOrigin;
        }
      } catch (err) {
        console.warn('Could not parse document.referrer as a URL:', document.referrer);
      }
    }

    return null;
  }, []);

  // Initialize component and notify parent (runs once on mount)
  useEffect(() => {
    // Notify parent that child is ready
    const notifyParentReady = () => {
      const allowedOrigins = process.env.REACT_APP_ALLOWED_ORIGINS.split(',');

      if (!parentOriginRef.current) {
        parentOriginRef.current = detectParentOrigin(allowedOrigins);
      }

      const message = {
        type: 'CHILD_READY',
        payload: {
          message: 'React liveness component is ready',
          timestamp: new Date().toISOString()
        }
      };

      // Only notify once the parent origin is known and allowed
      console.log('Notifying parent ready. Parent origin:', parentOriginRef.current, 'Allowed origins:', allowedOrigins);
      if (parentOriginRef.current && allowedOrigins.includes(parentOriginRef.current)) {
        window.parent.postMessage(message, parentOriginRef.current);
      }
    };

    // Send immediately and after a short delay
    notifyParentReady();
    const timer = setTimeout(notifyParentReady, 100);

    return () => {
      clearTimeout(timer);
    };
  }, []); // Empty dependency - runs only once

  // Update message listener when handleMessageEvent changes
  useEffect(() => {
    window.addEventListener('message', handleMessageEvent);
    return () => {
      window.removeEventListener('message', handleMessageEvent);
    };
  }, [handleMessageEvent]);

  // Handle liveness check completion
  const handleAnalysisComplete = useCallback(() => {
    sendMessageToParent('LIVENESS_COMPLETE', {
      success: true,
      sessionId: sessionId,
      timestamp: new Date().toISOString()
    });
  }, [sendMessageToParent, sessionId]);

  // Handle liveness errors
  const handleLivenessError = useCallback((event) => {
    sendMessageToParent('LIVENESS_ERROR', {
      error: event,
      sessionId: sessionId,
      timestamp: new Date().toISOString()
    });
  }, [sendMessageToParent, sessionId]);

  // Handle liveness start
  const handleLivenessStart = useCallback(() => {
    sendMessageToParent('LIVENESS_START', {
      success: true,
      timestamp: new Date().toISOString()
    });
  }, [sendMessageToParent]);

  // Render states
  if (error) {
    return (
      <div className="liveness-container">
        <h1>Error</h1>
        <p>{error}</p>
      </div>
    );
  }

  if(start) {
    if (!sessionId || !credentials || !config) {
      return (
        <div className="liveness-container">
          <h1>Waiting for credentials...</h1>
          <p>Please send credentials from parent application using SET_CREDENTIALS message.</p>
        </div>
      );
    }
  }
  else {
    return (
        <div className="liveness-start-screen">
          <div className="liveness-start-content">
            <h2 className="liveness-title">Welcome to Video Liveness Check</h2>
            <p className="liveness-desc">To verify your identity, please start the video check. Make sure you are in a well-lit area and your face is clearly visible.</p>
            <button className="liveness-start-btn" onClick={handleLivenessStart}>
              Start video check
            </button>
          </div>
        </div>
      )
  }

  return (
    <div className="liveness-container">
      {start &&
        <FaceLivenessDetector
          sessionId={sessionId}
          region={region}
          config={config}
          onAnalysisComplete={handleAnalysisComplete}
          onError={handleLivenessError}
          disableStartScreen={true}
        />
      }
    </div>
  );
}

export default Liveness;
